import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ExpenseCategory } from '../types/expense';
import {
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  type ExpenseCategoryCreateDto,
  type ExpenseCategoryPatchDto,
} from '../lib/api/endpoints';
import { expenseCategoryFromDto } from '../lib/api/mappers';

/**
 * FE-12 — el catálogo de categorías de gasto del tenant.
 *
 * Se pide SIEMPRE completo (con las inactivas) y se reparte en memoria, porque
 * las tres superficies que lo consumen necesitan cosas distintas:
 *
 *  - el **formulario** solo puede ofrecer las activas — una categoría
 *    desactivada se retiró a propósito;
 *  - **Administración** necesita ver las inactivas para poder reactivarlas;
 *  - el **filtro del listado** también las ofrece, porque un gasto viejo
 *    conserva su categoría aunque hoy esté retirada, y esconderla del filtro
 *    haría ese gasto imposible de encontrar.
 *
 * Pedirlo dos veces con `?active=` sería dos viajes por una lista de nueve
 * filas que cambia una vez al año.
 */
export function useExpenseCategories() {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const res = await getExpenseCategories();
        if (cancelled) return;
        setCategories(res.items.map(expenseCategoryFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setCategories([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando el catálogo de gastos'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  const upsert = useCallback((cat: ExpenseCategory) => {
    setCategories(prev => {
      const idx = prev.findIndex(c => c.id === cat.id);
      if (idx === -1) return [...prev, cat];
      const next = [...prev];
      next[idx] = cat;
      return next;
    });
  }, []);

  const acciones = useMemo(() => ({
    crear: async (data: ExpenseCategoryCreateDto): Promise<ExpenseCategory> => {
      const cat = expenseCategoryFromDto(await createExpenseCategory(data));
      upsert(cat);
      return cat;
    },
    // Solo la etiqueta y el interruptor: `code` no viaja porque no se corrige.
    // No hay borrado, y es a propósito: borrar una categoría reescribiría el
    // significado del histórico de gastos.
    actualizar: async (categoryId: string, data: ExpenseCategoryPatchDto): Promise<ExpenseCategory> => {
      const cat = expenseCategoryFromDto(await updateExpenseCategory(categoryId, data));
      upsert(cat);
      return cat;
    },
  }), [upsert]);

  const activas = useMemo(
    () => categories.filter(c => c.active).sort((a, b) => a.label.localeCompare(b.label)),
    [categories],
  );

  return { categories, activas, isLoading, error, reload, ...acciones };
}
